import React from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import SEO from "../components/SEO";

const items = [
  { q: "Who can purchase from ProSource Labs?", a: "Our products are sold only to qualified researchers and institutional accounts aged 21 or older. All purchasers must agree that compounds are used strictly for research purposes." },
  { q: "Are your products for human use?", a: "No. All compounds are sold strictly for laboratory research use only. They are not intended for human or animal consumption, diagnostic use, or therapeutic application." },
  { q: "Do you provide Certificates of Analysis?", a: "Yes. Independent HPLC and mass-spectrometry analysis is available for every lot on request. Contact support after placing an order." },
  {
    q: "Shipping Information",
    a: [
      "At ProSource Labs, we focus on fast processing, secure packaging, and reliable delivery.",
      "",
      "• Most orders ship within 1 business day after payment confirmation.",
      "• Orders placed on weekends or holidays process the next business day.",
      "• Tracking information is automatically emailed once your order ships.",
      "• Domestic orders are typically shipped via USPS Priority Mail or similar expedited services.",
      "• Estimated delivery time is 2–5 business days depending on location and carrier conditions.",
      "• Orders are fulfilled after payment verification is completed.",
      "• All shipments are securely and discreetly packaged to protect product integrity and customer privacy.",
      "• Wholesale or bulk orders may require additional processing time depending on order size and customization.",
      "",
      "For support or shipping questions, visit: https://prosourcelabs.org",
    ].join("\n"),
  },
  {
    q: "How should products be stored?",
    a: [
      "Lyophilized Peptides (Powder Form):",
      "• Short term (weeks to 12 months): store at 2–8°C (36–46°F) in a refrigerator.",
      "• Long term (12+ months): store at -20°C (-4°F) in a freezer.",
      "• Freezer storage is only necessary when long-term (12+ month) storage without reconstitution is planned.",
      "• Keep vials tightly sealed and protected from moisture.",
      "• Plastic storage containers with a rubber gasket and desiccant packs are highly recommended.",
      "",
      "Reconstituted Peptides (In Solution):",
      "• In use (up to 28 days): store at 2–8°C (36–46°F) in a refrigerator.",
      "• Never store reconstituted peptides in a freezer.",
      "",
      "Factors that can degrade peptide efficacy: heat, light, moisture, pH extremes, oxygen, repeated freeze–thaw cycles, and contamination.",
      "",
      "Do:",
      "• Store refrigerated at 2–8°C (36–46°F). Only lyophilized (powder) vials may be frozen at -20°C (-4°F) for long-term storage. Keep away from light and moisture.",
      "• Use an airtight container with desiccant.",
      "• Keep vials tightly sealed when not in use.",
      "• Allow reconstituted peptides to come to room temperature before use.",
      "• Use bacteriostatic water for reconstitution.",
      "• Label vials with peptide name, concentration, and reconstitution date.",
      "",
      "Don't:",
      "• Don't expose to heat or direct sunlight.",
      "• Don't store in bathroom or humid areas.",
      "• Don't shake vials vigorously.",
      "• Don't repeatedly freeze and thaw.",
      "• Don't use if solution is cloudy, discolored, or has particulates.",
      "• Don't leave reconstituted peptides at room temperature.",
      "",
      "For research purposes only.",
    ].join("\n"),
  },
  { q: "What payment methods do you accept?", a: "We accept PayPal and Venmo." },
  { q: "Do you ship internationally?", a: "Currently we serve domestic researchers only. International institutional inquiries can be sent to our support team for evaluation." },
  { q: "What if my package is damaged or lost?", a: "Contact support within 7 days of delivery with photographic evidence and we will replace or refund per our Refund Policy." },
];

/**
 * Render an answer string, supporting:
 * - Bullet lines starting with "• " or "- " or "* "
 * - Blank lines for paragraph spacing
 * - Bare URLs (linkified)
 */
function renderAnswer(text) {
  const lines = text.split("\n");
  const blocks = [];
  let listBuffer = [];

  const flushList = (key) => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul key={`ul-${key}`} className="list-none space-y-2 my-3 pl-1">
        {listBuffer.map((item, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-[#00C3FF] mt-1 select-none">▸</span>
            <span className="text-[#8A94A6] leading-relaxed">{linkify(item)}</span>
          </li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[•\-*]\s+(.*)$/);
    if (bullet) {
      listBuffer.push(bullet[1]);
    } else if (line === "") {
      flushList(idx);
    } else {
      flushList(idx);
      blocks.push(
        <p key={`p-${idx}`} className="leading-relaxed my-2">
          {linkify(line)}
        </p>
      );
    }
  });
  flushList("end");
  return blocks;
}

function linkify(text) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a
        key={i}
        href={p}
        target="_blank"
        rel="noreferrer"
        className="text-[#00C3FF] hover:underline break-all"
      >
        {p}
      </a>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

export default function FAQs() {
  // FAQ schema for rich snippets in Google SERP
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      <SEO
        title="FAQs — Peptide Research, Shipping, Compliance"
        description="Common questions about ProSource Labs research peptides: who can purchase, lab testing protocols, US shipping times, payment methods, refund policy, and research compliance."
        path="/faqs"
        jsonLd={faqJsonLd}
      />
      <div className="overline">FAQS</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3">Frequently Asked Questions</h1>
      <p className="text-sm text-[#8A94A6] mt-4 max-w-2xl">
        Answers to common questions about our compounds, ordering, shipping, and research compliance.
      </p>

      <Accordion type="single" collapsible className="mt-12">
        {items.map((it, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            data-testid={`faq-item-${i}`}
            className="border-b border-white/10"
          >
            <AccordionTrigger className="font-heading text-base sm:text-lg text-left hover:no-underline py-6 hover:text-[#00C3FF] transition-colors">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#8A94A6] text-sm pb-6">
              {renderAnswer(it.a)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
